import React, { useState, useRef, useEffect, useCallback } from "react";
import axios from "axios";
import { Sparkles, Send, Loader2, Trash2, Plus, X, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Welche Verpflegungspauschalen gelten aktuell in Deutschland?",
  "PKW oder Bahn – was lohnt sich für 300 km?",
  "Was muss ich bei Belegen für das Finanzamt beachten?",
  "Tipps für günstige Geschäftsreise-Flüge",
  "Welche Kosten kann ich als Reisekosten absetzen?",
];

export const TravelAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) setTimeout(() => inputRef.current?.focus(), 200);
  }, [isOpen]);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reise/assistant/chat`, {
        message: msg,
        conversation_id: conversationId,
      });
      setConversationId(res.data.conversation_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.message }]);
    } catch (err) {
      const detail = err.response?.data?.detail || "Verbindungsfehler – bitte erneut versuchen.";
      setMessages((prev) => [...prev, { role: "assistant", content: `⚠️ ${detail}`, isError: true }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, conversationId]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const resetChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  if (!isOpen) {
    return (
      <Card className="p-4 border-[var(--brand-border)] bg-gradient-to-br from-[var(--brand-bg)] to-white" data-testid="travel-assistant-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[var(--brand-primary)] flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-[var(--brand-ink)]">Travel KI Assistent</h3>
              <p className="text-xs text-[var(--brand-muted)]">Fragen zu Reisekosten, Pauschalen & Planung</p>
            </div>
          </div>
          <Button onClick={() => setIsOpen(true)} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white" data-testid="travel-assistant-open">
            <MessageCircle className="w-4 h-4 mr-1.5" /> Chat starten
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className="border-[var(--brand-border)] overflow-hidden" data-testid="travel-assistant-chat">
      {/* Header */}
      <div className="px-4 py-3 bg-[var(--brand-primary)] text-white flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4" />
          <span className="font-semibold text-sm">Travel KI Assistent</span>
        </div>
        <div className="flex items-center gap-1">
          {conversationId && (
            <button onClick={resetChat} className="text-white/70 hover:text-white text-xs px-2 py-1 rounded hover:bg-white/10 transition" title="Neuer Chat">
              <Plus className="w-3.5 h-3.5 inline mr-0.5" /> Neu
            </button>
          )}
          <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white p-1 rounded hover:bg-white/10 transition" title="Schließen">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="h-[400px] overflow-y-auto p-4 space-y-3 bg-[var(--brand-bg)]/30">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <Sparkles className="w-8 h-8 mx-auto mb-3 text-[var(--brand-primary)] opacity-50" />
            <p className="text-sm text-[var(--brand-muted)] mb-4">
              Frag mich alles rund um Geschäftsreisen – Pauschalen, Belegpflichten, Reiseplanung & mehr.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {SUGGESTIONS.slice(0, 3).map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[var(--brand-border)] bg-white hover:bg-[var(--brand-bg)] text-[var(--brand-ink)] transition"
                  data-testid={`travel-suggestion-${i}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
              msg.role === "user"
                ? "bg-[var(--brand-primary)] text-white rounded-br-sm"
                : msg.isError
                  ? "bg-red-50 text-red-700 border border-red-200 rounded-bl-sm"
                  : "bg-white border border-[var(--brand-border-light)] text-[var(--brand-ink)] rounded-bl-sm shadow-sm"
            }`}>
              {msg.role === "assistant" ? (
                <div className="whitespace-pre-wrap" dangerouslySetInnerHTML={{
                  __html: msg.content
                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                    .replace(/\n- /g, '\n• ')
                    .replace(/\n(\d+)\. /g, '\n$1. ')
                }} />
              ) : (
                <span>{msg.content}</span>
              )}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-white border border-[var(--brand-border-light)] rounded-xl rounded-bl-sm px-4 py-3 shadow-sm">
              <div className="flex items-center gap-2 text-sm text-[var(--brand-muted)]">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                <span>Denkt nach …</span>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-3 border-t border-[var(--brand-border-light)] bg-white">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Frage zum Thema Geschäftsreise …"
            disabled={loading}
            className="flex-1 h-10 rounded-lg border border-[var(--brand-border)] px-3 text-sm focus:outline-none focus:border-[var(--brand-primary)] transition"
            data-testid="travel-assistant-input"
          />
          <Button onClick={() => sendMessage()} disabled={!input.trim() || loading}
            className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white h-10 px-4"
            data-testid="travel-assistant-send">
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {messages.length > 0 && messages.length < 4 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {SUGGESTIONS.filter((s) => !messages.some((m) => m.content === s)).slice(0, 2).map((s, i) => (
              <button key={i} onClick={() => sendMessage(s)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-[var(--brand-border-light)] bg-[var(--brand-bg)] hover:bg-white text-[var(--brand-muted)] transition">
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
